import { type ReactNode } from 'react';

export interface IIconProps {
	className?: string;
}

export function BaseIcon({
	className,
	children,
}: IIconProps & { children: ReactNode }) {
	return (
		<svg
			viewBox="0 0 24 24"
			width="20"
			height="20"
			fill="none"
			className={className}
			aria-hidden="true"
		>
			{children}
		</svg>
	);
}

export function ChevronDownIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="m6 9 6 6 6-6"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function CheckIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="m5 12.5 4.5 4.5L19 7"
				stroke="currentColor"
				strokeWidth="1.9"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function LogoutIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M15.5 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7.5a2 2 0 0 0 2-2v-2"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M9.5 12H21M17.5 8.3 21.2 12l-3.7 3.7"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function MenuIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M4 6.5h16M4 12h16M4 17.5h16"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
			/>
		</BaseIcon>
	);
}

export function CloseIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M6 6l12 12M18 6 6 18"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
			/>
		</BaseIcon>
	);
}

export function TeamIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M12 3.5 4 6.8v4.6c0 5 3.3 8.4 8 9.1 4.7-.7 8-4.1 8-9.1V6.8L12 3.5Z"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinejoin="round"
			/>
			<path
				d="M9 12.3l2.2 2.2 4-4.4"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function PlusIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M12 4.5v15M4.5 12h15"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
			/>
		</BaseIcon>
	);
}

export function ArrowLeftIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M19 12H5M11 6l-6 6 6 6"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function DashboardIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<rect
				x="3.5"
				y="3.5"
				width="7"
				height="7"
				rx="1.6"
				stroke="currentColor"
				strokeWidth="1.7"
			/>
			<rect
				x="13.5"
				y="3.5"
				width="7"
				height="7"
				rx="1.6"
				stroke="currentColor"
				strokeWidth="1.7"
			/>
			<rect
				x="3.5"
				y="13.5"
				width="7"
				height="7"
				rx="1.6"
				stroke="currentColor"
				strokeWidth="1.7"
			/>
			<rect
				x="13.5"
				y="13.5"
				width="7"
				height="7"
				rx="1.6"
				stroke="currentColor"
				strokeWidth="1.7"
			/>
		</BaseIcon>
	);
}

export function CustomersIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.7" />
			<path
				d="M3.5 19c.6-3 2.7-4.6 5.5-4.6S14 16 14.6 19M15.5 8.8a2.8 2.8 0 1 0 0-5.6M16.6 14.6c2.3.4 3.8 1.9 4.3 4.4"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinecap="round"
			/>
		</BaseIcon>
	);
}

export function InventoryIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M3.5 7.5 12 3l8.5 4.5v9L12 21l-8.5-4.5v-9Z"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinejoin="round"
			/>
			<path
				d="M3.8 7.4 12 12l8.2-4.6M12 12v9"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function SalesIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M3.5 6h2l1 11.2a1.6 1.6 0 0 0 1.6 1.5H18a1.6 1.6 0 0 0 1.6-1.3l1.3-7.4H6.5"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<circle cx="9.5" cy="21" r="1.2" fill="currentColor" />
			<circle cx="17" cy="21" r="1.2" fill="currentColor" />
		</BaseIcon>
	);
}

export function PurchaseOrderIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M3.5 8.5 8 5l4.5 3.5M3.5 8.5v7L8 19l4.5-3.5v-7M3.5 8.5 8 12l4.5-3.5"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinejoin="round"
			/>
			<path
				d="M15.5 12h5.5M18.2 9.3 21 12l-2.8 2.7"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function TransferOrderIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M4 8h13M13.5 4.5 17 8l-3.5 3.5"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M20 16H7M10.5 12.5 7 16l3.5 3.5"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function SettingsIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.7" />
			<path
				d="M12 3.8v2M12 18.2v2M20.2 12h-2M5.8 12h-2M17.6 6.4l-1.4 1.4M7.8 16.2l-1.4 1.4M17.6 17.6l-1.4-1.4M7.8 7.8 6.4 6.4"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinecap="round"
			/>
		</BaseIcon>
	);
}

export function BusinessIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M4 10 5.6 4h12.8L20 10"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M4 10h16v2.4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10Z"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinejoin="round"
			/>
			<path
				d="M5.5 14.4V20h13v-5.6"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinejoin="round"
			/>
			<path
				d="M10 20v-4.5h4V20"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function BillingIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<rect
				x="3"
				y="5.5"
				width="18"
				height="13"
				rx="2"
				stroke="currentColor"
				strokeWidth="1.7"
			/>
			<path d="M3 9.5h18" stroke="currentColor" strokeWidth="1.7" />
			<path
				d="M6.5 14.2h4"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinecap="round"
			/>
		</BaseIcon>
	);
}

export function PersonIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.7" />
			<path
				d="M4.5 20c0-3.6 3.4-6.5 7.5-6.5s7.5 2.9 7.5 6.5"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinecap="round"
			/>
		</BaseIcon>
	);
}

export function BriefcaseIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<rect
				x="3.5"
				y="7.5"
				width="17"
				height="11"
				rx="2"
				stroke="currentColor"
				strokeWidth="1.7"
			/>
			<path
				d="M8.5 7.5V6a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1.5"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinecap="round"
			/>
			<path d="M3.5 12.5h17" stroke="currentColor" strokeWidth="1.7" />
		</BaseIcon>
	);
}

export function EyeIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"
				stroke="currentColor"
				strokeWidth="1.6"
				strokeLinejoin="round"
			/>
			<circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" />
		</BaseIcon>
	);
}

export function EyeOffIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M3 3l18 18M10.6 10.6a3 3 0 0 0 4.24 4.24M9.4 4.7A10.4 10.4 0 0 1 12 4.5c6.5 0 10 7 10 7a18 18 0 0 1-3.1 4.1M6.3 6.3A18 18 0 0 0 2 11.5s3.5 7 10 7a10 10 0 0 0 4.2-.9"
				stroke="currentColor"
				strokeWidth="1.6"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function ArrowRightIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M5 12h14M13 6l6 6-6 6"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function ReceiptIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M6 3h12v18l-3-2-3 2-3-2-3 2V3Z M9 8h6 M9 12h6"
				stroke="currentColor"
				strokeWidth="1.7"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function ScanIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M4 8V5a1 1 0 0 1 1-1h3 M16 4h3a1 1 0 0 1 1 1v3 M20 16v3a1 1 0 0 1-1 1h-3 M8 20H5a1 1 0 0 1-1-1v-3 M7 12h10"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function SyncIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M20 11a8 8 0 0 0-14.3-4.9L4 8 M4 4v4h4 M4 13a8 8 0 0 0 14.3 4.9L20 16 M20 20v-4h-4"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function ChevronRightIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="m9 6 6 6-6 6"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function CalculatorIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M6 3h12a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z M8 6.5h8v3H8z M8.5 13h.01 M12 13h.01 M15.5 13h.01 M8.5 16.5h.01 M12 16.5h.01 M15.5 16.5h.01"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function PercentIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M19 5 5 19"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
			/>
			<circle cx="7" cy="7" r="2.3" stroke="currentColor" strokeWidth="1.8" />
			<circle cx="17" cy="17" r="2.3" stroke="currentColor" strokeWidth="1.8" />
		</BaseIcon>
	);
}

export function TrashIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M4 7h16 M9 7V4.5h6V7 M6.5 7l.8 12.2a1 1 0 0 0 1 .8h7.4a1 1 0 0 0 1-.8L17.5 7 M10 11v5 M14 11v5"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function BellIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2h-15L6 16Z M10 20.5a2.2 2.2 0 0 0 4 0"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function ReturnIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M9 14 4 9l5-5 M4 9h10.5a5.5 5.5 0 0 1 0 11H11"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function CopyIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<rect
				x="8"
				y="8"
				width="12"
				height="12"
				rx="2"
				stroke="currentColor"
				strokeWidth="1.8"
			/>
			<path
				d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function WifiOffIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M1 1l22 22 M16.72 11.06a10.94 10.94 0 0 1 2.28 1.49 M5 12.55a10.94 10.94 0 0 1 5.17-2.39 M10.71 5.05A16 16 0 0 1 22.58 9 M1.42 9a15.91 15.91 0 0 1 4.7-2.88 M8.53 16.11a6 6 0 0 1 6.95 0 M12 20h.01"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function UploadIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M12 16V4M7 9l5-5 5 5M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function DownloadIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M12 4v12M7 11l5 5 5-5M4 20h16"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function FileIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M13 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-6-5Z"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<path
				d="M13 3v5h6"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function PrinterIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M6 9V2h12v7 M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<rect
				x="6"
				y="14"
				width="12"
				height="8"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}

export function ExternalLinkIcon(props: IIconProps) {
	return (
		<BaseIcon {...props}>
			<path
				d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6 M15 3h6v6 M10 14 21 3"
				stroke="currentColor"
				strokeWidth="1.8"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</BaseIcon>
	);
}
